import './ClickToCallDialog.scss';

import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';
import Dialog from 'fe-design-base/organisms/Dialog';
import { Form, Formik } from 'formik';
import Immutable from 'immutable';
import * as Yup from 'yup';

import { hideModal } from 'actions/modals';

import { getCurrentUser, getCurrentUserPhone } from 'selectors/session';

import { UtmCampaignType, UtmContentType } from 'features/calendlyEnterprise';
import {
  selectIsCheckHoursFulfilled,
  selectIsWithinBusinessHours,
} from 'features/help/selectors';

import Box from 'components/Box';

import { cxHelpers } from 'util/className';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { getUrlParamValue } from 'util/urlParams';
import { useExtendUx, useTrackUx, withUxRoot } from 'util/uxEvents';
import { phoneSchema } from 'util/yupHelpers';

import { checkHours as getCheckHours } from '../../help/actions';

import OutsideBusinessHours from './views/OutsideBusinessHours';
import { ViewType } from './views/types';
import WithinBusinessHoursRequest from './views/WithinBusinessHoursRequest';
import WithinBusinessHoursSuccess from './views/WithinBusinessHoursSuccess';
import { CLICK_TO_CALL_VIEW_KEY as VIEW_KEY } from './constants';

const { cx } = cxHelpers('ClickToCallDialog');
export const VIEWS = {
  [VIEW_KEY.outside_business_hours]: OutsideBusinessHours,
  [VIEW_KEY.within_business_hours_request]: WithinBusinessHoursRequest,
  [VIEW_KEY.within_business_hours_success]: WithinBusinessHoursSuccess,
};

const FormSchema = Yup.object().shape({ ...phoneSchema });

interface ClickToCallDialogProps {
  onSubmit?: boolean;
  isSubmitting?: boolean;
  viewPrefix?: '' | 'nbk_';
  talkdeskId?: string | null;
  initialPhoneNumber?: string;
  utmContent?: UtmContentType;
  openedFromURLParam?: boolean;
  utmCampaign?: UtmCampaignType;
  hideScheduleCall?: boolean;
  isWithinBusinessHours?: boolean;
  isCheckHoursFulfilled?: boolean;
  customTitleWithinBusinessHours?: string | null;
  customTitleOutsideBusinessHours?: string | null;
  outsideBusinessHoursCustomComponentKey?: string | null;
  closeClickToCallDialog: () => void;
  currentUser: Immutable.Map<string, string>;
  checkHours?: () => boolean | void | undefined;
}

const ClickToCallDialog = ({
  checkHours,
  utmContent,
  utmCampaign,
  currentUser,
  viewPrefix = '',
  hideScheduleCall,
  talkdeskId = null,
  initialPhoneNumber,
  openedFromURLParam,
  isWithinBusinessHours,
  isCheckHoursFulfilled,
  customTitleWithinBusinessHours = null,
  customTitleOutsideBusinessHours = null,
  outsideBusinessHoursCustomComponentKey = null,
  closeClickToCallDialog,
}: ClickToCallDialogProps) => {
  const [dialogTitle, setDialogTitle] = useState('');
  const [reload, setReload] = useState(true);
  const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
  const [currentViewKey, setCurrentViewKey] = useState('');
  const [dismissClicked, setDismissClicked] = useState(false);

  const talkdeskIdFromURL = getUrlParamValue('c2c-td-id');

  const extendUxEvent = useExtendUx();
  extendUxEvent({
    timing: isWithinBusinessHours ? 'business hours' : 'non-business hours',
    element: 'Commandbar',
  });

  const trackUx = useTrackUx();

  const handleClose = useCallback(() => {
    if (openedFromURLParam) {
      setDismissClicked(true);
    }

    trackUx(EVENT_ACTIONS.DISMISS_CLICKED, TRACK_ACTION_TYPES.CLICK);

    closeClickToCallDialog();
  }, [closeClickToCallDialog, openedFromURLParam, trackUx]);

  const CurrentView: ViewType = VIEWS[currentViewKey];

  useEffect(() => {
    if (reload) {
      checkHours?.();
      setReload(false);
    }
  }, [checkHours, reload]);

  useEffect(() => {
    setCurrentViewKey(
      isWithinBusinessHours
        ? VIEW_KEY[`${viewPrefix}within_business_hours_request`]
        : VIEW_KEY[`${viewPrefix}outside_business_hours`]
    );
  }, [isWithinBusinessHours, viewPrefix]);

  const onlyDigitsPhone = initialPhoneNumber?.replace(/\D/g, '');

  return (
    <Dialog
      width={600}
      className={cx()}
      title={dialogTitle}
      disableBackdropClick
      onClose={handleClose}
      open={openedFromURLParam ? !dismissClicked : true}
    >
      {() => (
        <Formik
          // eslint-disable-next-line max-len
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop,@typescript-eslint/no-empty-function
          onSubmit={() => {}}
          validationSchema={FormSchema}
          initialValues={{
            primaryDisabled: true,
            phone: onlyDigitsPhone?.length === 10 ? onlyDigitsPhone : null,
          }}
        >
          {({ isValid, dirty, values, isSubmitting }) =>
            isCheckHoursFulfilled ? (
              <Form>
                {CurrentView ? (
                  <CurrentView
                    dirty={dirty}
                    values={values}
                    isValid={isValid}
                    setReload={setReload}
                    checkHours={checkHours}
                    talkdeskId={
                      openedFromURLParam && !!talkdeskIdFromURL
                        ? talkdeskIdFromURL
                        : talkdeskId
                    }
                    utmContent={utmContent}
                    utmCampaign={utmCampaign}
                    currentUser={currentUser}
                    isSubmitting={isSubmitting}
                    setDialogTitle={setDialogTitle}
                    isPrimaryLoading={isPrimaryLoading}
                    closeClickToCallDialog={handleClose}
                    setCurrentViewKey={setCurrentViewKey}
                    setIsPrimaryLoading={setIsPrimaryLoading}
                    hideScheduleCall={hideScheduleCall}
                    closeDialogWithoutTracking={closeClickToCallDialog}
                    customTitleWithinBusinessHours={
                      customTitleWithinBusinessHours
                    }
                    customTitleOutsideBusinessHours={
                      customTitleOutsideBusinessHours
                    }
                    outsideBusinessHoursCustomComponentKey={
                      outsideBusinessHoursCustomComponentKey
                    }
                  />
                ) : null}
              </Form>
            ) : (
              <Box hcenter p={120}>
                <CircularInfinite size="medium" variant="dark" />
              </Box>
            )
          }
        </Formik>
      )}
    </Dialog>
  );
};

export const ComponentWithUxRoot = withUxRoot({
  productArea: PRODUCT_AREAS.MONETIZATION,
  eventCategory: EVENT_CATEGORIES.COMMAND_BAR,
  eventName: 'Click to call',
})(ClickToCallDialog);

export default connect(
  state => ({
    currentUser: getCurrentUser(state),
    initialPhoneNumber: getCurrentUserPhone(state),
    isWithinBusinessHours: selectIsWithinBusinessHours(state),
    isCheckHoursFulfilled: selectIsCheckHoursFulfilled(state),
  }),
  {
    checkHours: getCheckHours,
    closeClickToCallDialog: hideModal,
  }
)(ComponentWithUxRoot);
